// Lightweight UTM/source attribution helper.
// Captures UTM params on first touch, persists them for the session, and exposes
// `getAttribution()` for any form/checkout call to include alongside its payload.

const STORAGE_KEY = "ms_attribution";
const SESSION_KEY = "ms_session_id";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_path?: string;
  landing_slug?: string;
  first_seen_at?: string;
  session_id?: string;
};

function readStorage(): Attribution {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
}

function writeStorage(a: Attribution) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(a)); } catch { /* ignore */ }
}

export function sessionId(): string {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch { return `s_${Date.now()}`; }
}

/**
 * Capture attribution from the current URL.
 * - First-touch wins: only writes UTM/referrer once per browser.
 * - Always refreshes `landing_path` / `landing_slug` so we know the
 *   most recent entry point.
 */
export function captureAttribution(opts?: { slug?: string }): Attribution {
  if (typeof window === "undefined") return {};
  const stored = readStorage();
  const url = new URL(window.location.href);
  const utm: Partial<Attribution> = {};
  for (const k of UTM_KEYS) {
    const v = url.searchParams.get(k);
    if (v) (utm as any)[k] = v.slice(0, 200);
  }
  const hasFirstTouch = !!(stored.utm_source || stored.utm_medium || stored.utm_campaign);
  const next: Attribution = {
    ...stored,
    ...(hasFirstTouch ? {} : utm),
    ...(hasFirstTouch ? {} : {
      referrer: document.referrer ? document.referrer.slice(0, 500) : stored.referrer,
      first_seen_at: stored.first_seen_at ?? new Date().toISOString(),
    }),
    landing_path: url.pathname + url.search,
    landing_slug: opts?.slug ?? stored.landing_slug,
    session_id: sessionId(),
  };
  writeStorage(next);
  return next;
}

export function getAttribution(): Attribution {
  const stored = readStorage();
  return { ...stored, session_id: sessionId() };
}